import { FunctionComponent } from 'react';
import { BubbleMenu } from '@tiptap/react/menus';
import type { Editor } from '@tiptap/react';
import { LuBold, LuItalic, LuStrikethrough, LuUnderline } from 'react-icons/lu';
import { IoIosClose } from 'react-icons/io';
import { cn } from '../../../../../../utilities/cn';
import {
  checkSelectionHasFormat,
  getSelectedTextColor,
} from '../../../kids/utils/editor.utils';

export const convertLegacyHTMLToTipTap = (html: unknown): string => {
  if (typeof html !== 'string' || !html) return '';

  let converted = html;

  converted = converted.replace(
    /<font\s+([^>]*\s+)?backColor=["']([^"']+)["']([^>]*)>/gi,
    (_match, before, color, after) => {
      const attrs = ((before || '') + (after || ''))
        .replace(/\s*backColor=["'][^"']+["']/gi, '')
        .trim();
      return `<mark data-color="${color}"${attrs ? ` ${attrs}` : ''}>`;
    },
  );

  converted = converted.replace(
    /<font\s+([^>]*\s+)?color=["']([^"']+)["']([^>]*)>/gi,
    (_match, before, color, after) => {
      const attrs = ((before || '') + (after || ''))
        .replace(/\s*color=["'][^"']+["']/gi, '')
        .trim();

      let styleAttr = `style="color: ${color}"`;
      if (attrs.includes('style=')) {
        const styleMatch = attrs.match(/style=["']([^"']*)["']/i);
        if (styleMatch && !styleMatch[1].includes('color:')) {
          styleAttr = `style="${styleMatch[1]}; color: ${color}"`;
        } else if (styleMatch) {
          styleAttr = styleMatch[0];
        }
      }

      const otherAttrs = attrs.replace(/style=["'][^"']*["']/gi, '').trim();
      return `<span ${styleAttr}${otherAttrs ? ` ${otherAttrs}` : ''}>`;
    },
  );

  converted = converted.replace(/<font([^>]*)>/gi, '<span$1>');

  converted = converted.replace(/<\/font>/gi, (_match, offset, string) => {
    const before = string.substring(0, offset);
    const lastMark = before.lastIndexOf('<mark');
    const lastFont = before.lastIndexOf('<font');

    const markCount =
      (before.match(/<mark[^>]*>/gi) || []).length -
      (before.match(/<\/mark>/gi) || []).length;
    const spanCount =
      (before.match(/<span[^>]*>/gi) || []).length -
      (before.match(/<\/span>/gi) || []).length;

    if (markCount > spanCount && lastMark > lastFont) {
      return '</mark>';
    }
    return '</span>';
  });

  return converted;
};

interface ToolbarButtonProps {
  icon: React.ReactNode;
  isActive?: boolean;
  onClick: () => void;
  disabled?: boolean;
  ariaLabel?: string;
}

export const RichTextToolbarButton: FunctionComponent<ToolbarButtonProps> = ({
  icon,
  isActive,
  onClick,
  disabled,
  ariaLabel,
}) => (
  <button
    type="button"
    aria-label={ariaLabel}
    disabled={disabled}
    onMouseDown={(e) => {
      e.preventDefault();
      if (!disabled) onClick();
    }}
    className={cn(
      'flex h-8 w-8 items-center justify-center rounded-md border border-transparent transition-all hover:border-gray-200 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40',
      {
        'border-blue-300 bg-blue-50 text-blue-700 shadow-sm': isActive,
      },
    )}
  >
    {icon}
  </button>
);

interface RichTextBubbleMenuProps {
  editor: Editor;
  selectedHTML: string;
  menuId: string | number;
}

export const RichTextBubbleMenu: FunctionComponent<RichTextBubbleMenuProps> = ({
  editor,
  selectedHTML,
  menuId,
}) => (
  <BubbleMenu
    editor={editor}
    className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-2 shadow-lg"
  >
    <RichTextToolbarButton
      icon={<LuBold size={18} />}
      isActive={checkSelectionHasFormat(selectedHTML, 'bold')}
      onClick={() => editor.chain().focus().toggleBold().run()}
      ariaLabel="Bold"
    />
    <RichTextToolbarButton
      icon={<LuItalic size={18} />}
      isActive={checkSelectionHasFormat(selectedHTML, 'italic')}
      onClick={() => editor.chain().focus().toggleItalic().run()}
      ariaLabel="Italic"
    />
    <RichTextToolbarButton
      icon={<LuUnderline size={18} />}
      isActive={checkSelectionHasFormat(selectedHTML, 'underline')}
      onClick={() => editor.chain().focus().toggleUnderline().run()}
      ariaLabel="Underline"
    />
    <RichTextToolbarButton
      icon={<LuStrikethrough size={18} />}
      isActive={checkSelectionHasFormat(selectedHTML, 'strike')}
      onClick={() => editor.chain().focus().toggleStrike().run()}
      ariaLabel="Strikethrough"
    />

    <div className="mx-2 h-6 w-px bg-gray-200" />

    <div className="flex items-center gap-1.5">
      <label
        className="text-xs font-medium text-gray-600"
        htmlFor={`textColor-${menuId}`}
      >
        Color
      </label>
      <input
        id={`textColor-${menuId}`}
        type="color"
        className="h-7 w-7 cursor-pointer rounded border border-gray-300 p-0 shadow-sm transition-shadow hover:shadow-md"
        value={(() => {
          let color = getSelectedTextColor(selectedHTML);

          if (color.startsWith('rgb')) {
            const rgb = color.match(/\d+/g)?.map(Number) || [0, 0, 0];
            color =
              '#' +
              rgb
                .slice(0, 3)
                .map((v) => v.toString(16).padStart(2, '0'))
                .join('');
          }

          return color;
        })()}
        onChange={(e) => {
          const nextColor = e.target.value || '#000000';
          editor.chain().focus().setColor(nextColor).run();
        }}
      />
    </div>

    <div className="ml-2 flex items-center gap-1.5">
      <label
        className="text-xs font-medium text-gray-600"
        htmlFor={`highlightColor-${menuId}`}
      >
        Highlight
      </label>
      <div className="flex items-center gap-1">
        <input
          id={`highlightColor-${menuId}`}
          type="color"
          className="h-7 w-7 cursor-pointer rounded border border-gray-300 p-0 shadow-sm transition-shadow hover:shadow-md"
          value={(() => {
            const match = selectedHTML?.match(
              /<mark[^>]*data-color=["'](#[0-9A-Fa-f]{3,6})["']/i,
            );
            return match ? match[1] : '#ffff00';
          })()}
          onChange={(e) => {
            const nextColor = e.target.value || '#ffff00';
            editor.chain().focus().setHighlight({ color: nextColor }).run();
          }}
        />
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            editor.chain().focus().unsetHighlight().run();
          }}
          disabled={!/<mark[^>]*>/i.test(selectedHTML || '')}
          aria-label="Remove highlight from selection"
          title="Remove highlight from selection only (keeps other formatting)"
          className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 bg-gray-50 text-gray-500 transition-all hover:border-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-gray-50 disabled:hover:text-gray-500"
        >
          <IoIosClose size={16} />
        </button>
      </div>
    </div>
  </BubbleMenu>
);
